import { useEffect, useRef } from 'react';

// Global stack of stable modal ids — only the topmost handles Esc / outside click
const activeModals = [];
let nextModalId = 0;

/**
 * Outside-click + Escape close for modals/drawers.
 * Uses a stable stack id (not onClose identity) so parent re-renders
 * cannot reorder nested overlays and dismiss the wrong layer.
 */
export function useModalClose({
  isOpen,
  onClose,
  isDirty = false,
  closeOnOutside = true,
}) {
  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const isDirtyRef = useRef(isDirty);
  const closeOnOutsideRef = useRef(closeOnOutside);
  const idRef = useRef(null);

  if (idRef.current === null) {
    idRef.current = ++nextModalId;
  }

  onCloseRef.current = onClose;
  isDirtyRef.current = isDirty;
  closeOnOutsideRef.current = closeOnOutside;

  useEffect(() => {
    if (!isOpen || !onCloseRef.current) return undefined;

    const id = idRef.current;
    activeModals.push(id);

    const isTopModal = () => activeModals[activeModals.length - 1] === id;

    const isExemptOutsideTarget = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest('[data-portal-dropdown="true"]') ||
        target.closest('.react-datepicker-popper'),
      );
    };

    /** Click landed inside another overlay panel (nested / portal sibling) */
    const isInsideOtherOverlay = (target) => {
      if (!(target instanceof Element) || !modalRef.current) return false;
      const otherRoot = target.closest('[data-modal-root], [data-drawer-root]');
      return Boolean(otherRoot && otherRoot !== modalRef.current);
    };

    const handleKeyDown = (e) => {
      if (!isTopModal()) return;
      if (
        e.key === 'Escape' &&
        !isDirtyRef.current &&
        closeOnOutsideRef.current
      ) {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };

    let swallowClick = null;
    const clearSwallow = () => {
      if (!swallowClick) return;
      document.removeEventListener('click', swallowClick, true);
      swallowClick = null;
    };

    const handleMouseDown = (e) => {
      if (!closeOnOutsideRef.current) return;
      if (!isTopModal()) return;
      if (!modalRef.current || modalRef.current.contains(e.target)) return;
      if (isExemptOutsideTarget(e.target)) return;
      // Don't close when interacting with a nested/sibling overlay panel
      if (isInsideOtherOverlay(e.target)) return;
      if (isDirtyRef.current) return;

      e.stopPropagation();

      // Overlay often loses pointer-events on close. Swallow the leftover
      // click so it cannot hit whatever sits underneath (calendar, buttons).
      clearSwallow();
      swallowClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        clickEvent.stopImmediatePropagation();
        clearSwallow();
      };
      document.addEventListener('click', swallowClick, true);
      window.setTimeout(clearSwallow, 0);

      onCloseRef.current?.();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('touchstart', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('touchstart', handleMouseDown);
      clearSwallow();
      const index = activeModals.indexOf(id);
      if (index > -1) {
        activeModals.splice(index, 1);
      }
    };
  }, [isOpen]);

  return modalRef;
}
